import React, {useEffect, useContext, useState} from 'react';
import {FiUpload, FiDownload, FiTrash2} from 'react-icons/fi';
import {VendorFileContext} from '../../context/VendorFileProvider';
import FileModal from '../../components/modals/FileModal';
import DeleteModal from '../../components/modals/DeleteModal';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';
import ExportButtonLarge from '../../components/buttons/ExportButtonLarge';

export default function VendorFiles({vendorId}) {
  const {
    vendorFiles = [],
    fileTypes,
    getFileTypes,
    handleFileUpload,
    deleteFile,
    downloadFile,
    getVendorFiles,
    formatDate,
    formatFileSize,
  } = useContext(VendorFileContext);
  const [selectedFileType, setSelectedFileType] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBrowseOpen, setIsBrowseOpen] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileIdToDelete, setFileIdToDelete] = useState(null);
  const isUploadButtonVisible = selectedFile && selectedFileType;
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  useEffect(() => {
    if (vendorId) {
      getVendorFiles(vendorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  useEffect(() => {
    getFileTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (vendorId) {
      getVendorFiles(vendorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || !selectedFileType) {
      return;
    }

    const allowedFileTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/plain',
      'image/png',
      'image/gif',
      'image/jpeg',
      'image/jpeg',
      'image/webp',
      'application/pdf',
      'application/vnd.oasis.opendocument.text',
      'application/vnd.oasis.opendocument.spreadsheet-template',
    ];

    const fileType = selectedFile.type;
    if (!allowedFileTypes.includes(fileType)) {
      setShowError(true);
      setText('Invalid file type. Only certain file types are allowed.');
      return;
    }

    const fileSizeInBytes = selectedFile.size;
    const maxSizeInBytes = 10 * 1024 * 1024;
    if (fileSizeInBytes > maxSizeInBytes) {
      setShowError(true);
      setText('File size exceeds the limit of 10MB.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('documentType', selectedFileType);
    handleFileUpload(formData, vendorId)
      .then(() => {
        setShowSuccess(true);
        setText('File uploaded successfully');
        closeBrowseModal();
        getVendorFiles(vendorId); // Fetch updated files after successful upload
      })
      .catch((error) => {
        setShowError(true);
        setText(`${error.message}`);
      });
  };

  const handleFileTypeChange = (e) => {
    const newValue = e.target.value;
    setSelectedFileType(newValue);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleDownload = (file) => {
    downloadFile(file);
  };

  const openBrowseModal = () => {
    if (!isBrowseOpen) {
      setIsBrowseOpen(true);
    }
  };

  const closeBrowseModal = () => {
    setSelectedFileType('');
    setSelectedFile(null);
    setIsDraggingOver(false);
    setIsBrowseOpen(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = () => {
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      setSelectedFile(droppedFiles[0]);
    }
  };

  const handleDeleteComment = (fileId) => {
    setFileIdToDelete(fileId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmation = (rowKey) => {
    deleteFile(rowKey).then(() => {
      getVendorFiles(vendorId); // Fetch updated files after deletion
    });
    setShowDeleteModal(false);
  };

  const exportData =
    vendorFiles?.map((file) => ({
      fileName: file.label,
      fileType:
        fileTypes.find((fileType) => fileType.id === file.type)?.type || 'N/A',
      uploadedBy: file.uploadedBy,
      size: formatFileSize(file.size),
      date: formatDate(file.uploadDate),
    })) || [];

  return (
    <div className="modal mb-12 mt-3 flex h-full flex-col">
      <div className="modal-content flex-grow p-4">
        {vendorFiles && vendorFiles.length > 0 ? (
          <div>
            {!isBrowseOpen && (
              <div className="mb-4 flex">
                <label className="primary-large">
                  <FiUpload className="mr-2"/>
                  <span>Upload File</span>
                  <input onClick={openBrowseModal} className="hidden"/>
                </label>
                <ExportButtonLarge
                  excelData={exportData}
                  fileName={'VendorFiles'}
                />
              </div>
            )}
            <div className="overflow-x-auto">
              <div
                className="relative h-[650px] overflow-y-auto"
              >
                <table
                  className="min-w-full divide-y divide-gray-200 text-left text-sm text-gray-500 dark:text-gray-300">
                  <thead className="vendor-table-header">
                  <tr>
                    <th className="vendor-table-label truncate">File Name</th>
                    <th className="vendor-table-label">File Types</th>
                    <th className="vendor-table-label hidden lg:table-cell">
                      Uploaded By
                    </th>
                    <th className="vendor-table-label hidden lg:table-cell">
                      Size
                    </th>
                    <th className="vendor-table-label hidden lg:table-cell">
                      Date
                    </th>
                    <th className="vendor-table-label">Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  {vendorFiles.map((file, index) => (
                    <tr key={index} className="vendor-table-row">
                      <td className="truncate px-2 py-2" title={file.label}>
                        {file.label.length > 15
                          ? `${file.label.substring(0, 15)}...`
                          : file.label}
                      </td>
                      <td
                        className="w-20 truncate px-2 py-2"
                        title={file.type}
                      >
                        {file.type
                          ? fileTypes.find(
                            (fileType) => fileType.id === file.type
                          )?.type?.length > 20
                            ? fileTypes
                            .find((fileType) => fileType.id === file.type)
                            ?.type?.substr(0, 20) + '...'
                            : fileTypes.find(
                              (fileType) => fileType.id === file.type
                            )?.type
                          : 'N/A'}
                      </td>
                      <td
                        className="hidden truncate px-2 py-2 lg:table-cell"
                        title={
                          file.uploadedBy ? file.uploadedBy : 'Deleted User'
                        }
                      >
                        {file.uploadedBy &&
                        file.uploadedBy !== 'Deleted User' ? (
                          file.uploadedBy
                        ) : (
                          <span className="font-bold text-red-500">
                              Deleted User
                            </span>
                        )}
                      </td>

                      <td className="hidden w-20 truncate px-2 py-2 lg:table-cell">
                        {formatFileSize(file.size)}
                      </td>
                      <td className="hidden w-20 truncate px-2 py-2 lg:table-cell">
                        {formatDate(file.uploadDate)}
                      </td>
                      <td className="flex items-center space-x-2 px-2 py-2">
                        <button
                          onClick={() => handleDownload(file)}
                          className="mr-2 flex items-center rounded px-2 py-2 font-semibold text-blue-500 hover:text-blue-400"
                        >
                          <FiDownload/>
                        </button>
                        <button
                          onClick={() => handleDeleteComment(file.rowKey)}
                          className="flex items-center rounded px-2 py-2 font-semibold text-red-500 hover:text-red-400"
                        >
                          <FiTrash2/>
                        </button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div
            className="mb-6 mt-6 flex w-1/3 flex-col items-center justify-center space-y-4 rounded-md bg-gray-100 py-32 text-center text-gray-500 dark:bg-stone-800 dark:text-gray-200 sm:w-1/3 md:w-1/2 lg:mx-auto lg:w-3/4">
            <p>No Files Added.</p>
            <div className="flex items-center space-x-4">
              <label className="primary-large">
                <FiUpload className="mr-2"/>
                <span>Upload File</span>
                <input onClick={openBrowseModal} className="hidden"/>
              </label>
            </div>
          </div>
        )}
      </div>
      {isBrowseOpen && (
        <FileModal
          isOpen={isBrowseOpen}
          onCancel={closeBrowseModal}
          onSubmit={handleFileUpload}
          selectedFileType={selectedFileType}
          handleFileTypeChange={handleFileTypeChange}
          fileTypes={fileTypes}
        >
          <form onSubmit={handleSubmit}>
            <div className="m-14">
              <div
                className={`flex items-center space-x-4 border-2 border-dashed ${
                  isDraggingOver
                    ? 'border-indigo-600'
                    : 'border-sky-300 dark:border-indigo-300'
                } rounded-md p-28`}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label
                  className="flex w-28 cursor-pointer items-center rounded bg-blue-500 px-4 py-2 text-white transition-colors duration-200 hover:bg-blue-700 hover:text-white dark:bg-indigo-500 dark:hover:bg-indigo-600">
                  <FiUpload className="mr-2"/>
                  <span>Browse</span>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    id={`file-type-input-${
                      selectedFile ? selectedFile.name : 'default'
                    }`}
                    disabled={isDraggingOver}
                  />
                </label>
                <div className="flex flex-col items-start space-y-2">
                  <select
                    id="file-type-input"
                    className="mt-8 block w-48 rounded-md border border-gray-300 bg-gray-100 py-2.5 pl-2 dark:border-stone-700 dark:bg-stone-700"
                    name="status"
                    type="text"
                    value={selectedFileType}
                    onChange={handleFileTypeChange}
                  >
                    <option value="">Select File Type</option>
                    {fileTypes.map((type) => (
                      <option value={type.id} key={type.id}>
                        {type.type}
                      </option>
                    ))}
                  </select>
                  <div className="h-6">
                    {selectedFile && (
                      <p
                        className="mt-1 max-w-48 cursor-pointer truncate text-gray-500 hover:max-w-none dark:text-gray-300"
                        title={selectedFile ? selectedFile.name : ''}
                      >
                        {selectedFile.name.length > 20
                          ? selectedFile.name.substring(0, 20) + '...'
                          : selectedFile.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-10 flex flex-row items-end">
                  {isUploadButtonVisible ? (
                    <button
                      type="submit"
                      className="btn absolute rounded-md border border-gray-600 px-3 py-2 transition-all hover:bg-gray-100 dark:bg-gray-500 dark:hover:bg-gray-600"
                    >
                      Upload
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="absolute bottom-16 right-32 p-2 text-sm text-gray-500 dark:text-gray-500">
                Max file size: 10MB
              </div>
            </div>
          </form>
        </FileModal>
      )}
      {showDeleteModal && (
        <DeleteModal
          title="Are you sure?"
          onCancel={() => setShowDeleteModal(false)}
          onDelete={() => handleDeleteConfirmation(fileIdToDelete)}
        ></DeleteModal>
      )}
      {showSuccess && (
        <SuccessNotification
          show={showSuccess}
          onClose={closeNotification}
          text={text}
        />
      )}
      {showError && (
        <ErrorNotification
          show={showError}
          onClose={closeNotification}
          text={text}
        />
      )}
    </div>
  );
}
